import {useCallback, useState} from 'react';

import {isFunction} from 'ramda-adjunct';

import {useCallbackRef} from './useCallbackRef';

type StateUpdater<T> = T | ((previous: T | undefined) => T);
type UseControllableStateResult<T> = [T | undefined, (next: StateUpdater<T>) => void];

export interface UseControllableStateProps<T> {
  /**
   * The value to used in controlled mode
   */
  value?: T;
  /**
   * The initial value to be used, in uncontrolled mode
   */
  defaultValue?: T | (() => T);
  /**
   * The callback fired when the value changes
   */
  onChange?: (value: T) => void;
  /**
   * The function that determines if the state should be updated
   */
  shouldUpdate?: (prev: T | undefined, next: T) => boolean;
}

/**
 * React hook for using controlling component state.
 * @param props
 */
export function useControllableState<T>(
  props: UseControllableStateProps<T>
): UseControllableStateResult<T> {
  const shouldUpdate = props.shouldUpdate ?? ((prev, next) => prev !== next);
  const onChangeProp = useCallbackRef(props.onChange);
  const shouldUpdateProp = useCallbackRef(shouldUpdate);

  const [valueState, setValue] = useState<T | undefined>(props.defaultValue);

  const isControlled = props.value !== undefined;
  const value = isControlled ? props.value : valueState;

  // parity: legacy captured-setter semantics; fixing the stale closure is a tracked follow-up
  const updateValue = useCallback(
    (next: StateUpdater<T>) => {
      const nextValue = runIfFn(next, value);

      if (shouldUpdateProp(value, nextValue) === false) {
        return;
      }

      if (!isControlled) {
        setValue(nextValue);
      }

      onChangeProp(nextValue);
    },
    [isControlled, onChangeProp, shouldUpdateProp, value]
  );

  return [value, updateValue];
}

export function runIfFn<T>(valueOrFn: StateUpdater<T>, value: T | undefined): T {
  return isFunction(valueOrFn) ? valueOrFn(value) : valueOrFn;
}
